import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useWorkspace } from '../workspace';

type OrgSwitcherVariant = 'chip' | 'panel' | 'menu' | 'readonly';

function orgDisplayName(row: { name?: string; handle?: string } | null | undefined): string {
  const name = String(row?.name || '').trim();
  if (name) return name;
  const handle = String(row?.handle || '').trim();
  return handle || 'Select org';
}

/**
 * Current org from /_auth/tree. chip/menu switch; panel/readonly for settings page.
 */
export default function OrgSwitcher({ variant = 'chip' }: { variant?: OrgSwitcherVariant }) {
  const { orgs, org, selectedOrg, setOrg, loading, error } = useWorkspace();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = selectedOrg
    ? orgDisplayName(selectedOrg)
    : loading
      ? 'Loading…'
      : error
        ? 'Org unavailable'
        : 'Select org';

  const pickOrg = (orgId: string) => {
    if (orgId === org) {
      setOpen(false);
      return;
    }
    setOrg(orgId);
    window.location.reload();
  };

  if (variant === 'readonly') {
    return (
      <p className="text-sm text-c3-mute flex items-center gap-2 flex-wrap">
        Current org:
        <span className="c3-org-chip">{label}</span>
      </p>
    );
  }

  if (variant === 'panel') {
    return (
      <div className="space-y-2">
        <p className="text-sm text-c3-mute flex items-center gap-2 flex-wrap">
          Current org:
          <span className="c3-org-chip">{label}</span>
        </p>
        <ul className="space-y-1" role="listbox" aria-label="Organizations">
          {orgs.length === 0 ? (
            <li className="text-c3-mute text-sm">No organizations</li>
          ) : (
            orgs.map((row) => {
              const selected = row.org_id === org;
              return (
                <li key={row.org_id}>
                  <button
                    type="button"
                    className={`w-full text-left rounded-lg border px-3 py-2 ${
                      selected ? 'border-c3-purple bg-acc/10' : 'border-c3-tile-bd bg-c3-tile'
                    }`}
                    onClick={() => pickOrg(row.org_id)}
                  >
                    <div>{orgDisplayName(row)}</div>
                    {row.handle ? (
                      <div className="text-[12px] text-c3-mute">{row.handle}</div>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </div>
    );
  }

  if (variant === 'menu') {
    return (
      <div className="c3-org-switch c3-org-switch--menu" ref={rootRef}>
        <button
          type="button"
          className="c3-nav-menu-item c3-org-switch-menu-trigger"
          role="menuitem"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          title={selectedOrg ? `${orgDisplayName(selectedOrg)} (${selectedOrg.org_id})` : label}
        >
          <span className="c3-org-switch-menu-row">
            <span className="c3-org-switch-menu-k">Org</span>
            <span className="c3-org-chip">{label}</span>
            <ChevronDown className="c3-org-switch-chevron" aria-hidden />
          </span>
        </button>
        {open && (
          <ul className="c3-org-switch-menu c3-org-switch-menu--inline" role="listbox" aria-label="Organizations">
            {orgs.length === 0 ? (
              <li className="c3-org-switch-empty">No organizations</li>
            ) : (
              orgs.map((row) => {
                const selected = row.org_id === org;
                return (
                  <li key={row.org_id} role="option" aria-selected={selected}>
                    <button
                      type="button"
                      className={`c3-org-switch-item c3-org-switch-item--menu${selected ? ' is-selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        pickOrg(row.org_id);
                      }}
                    >
                      <span className="c3-org-switch-item-name">{orgDisplayName(row)}</span>
                      {row.handle ? (
                        <span className="c3-org-switch-item-handle">{row.handle}</span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="c3-org-switch" ref={rootRef}>
      <button
        type="button"
        className="c3-org-chip c3-org-switch-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        title={selectedOrg ? `${orgDisplayName(selectedOrg)} (${selectedOrg.org_id})` : label}
      >
        <span className="c3-org-switch-label">{label}</span>
        <ChevronDown className="c3-org-switch-chevron" aria-hidden />
      </button>
      {open && (
        <ul className="c3-org-switch-menu" role="listbox" aria-label="Organizations">
          {orgs.length === 0 ? (
            <li className="c3-org-switch-empty">No organizations</li>
          ) : (
            orgs.map((row) => {
              const selected = row.org_id === org;
              return (
                <li key={row.org_id} role="option" aria-selected={selected}>
                  <button
                    type="button"
                    className={`c3-org-switch-item${selected ? ' is-selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      pickOrg(row.org_id);
                    }}
                  >
                    <span className="c3-org-switch-item-name">{orgDisplayName(row)}</span>
                    {row.handle ? (
                      <span className="c3-org-switch-item-handle">{row.handle}</span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
